<script setup>
import AutocompleteFilter from '@/components/widgets/AutocompleteFilter.vue';
import { inject, onMounted, ref, watch } from 'vue';
import { useInfinite } from '@/composables/use-infinity';

const backendApi = inject('backendApi');

const emit = defineEmits(['update']);
const props = defineProps({
    hideLabel: {
        type: Boolean,
        default: false
    },
    itemTitle: {
        default: 'name'
    },
    itemValue: {
        default: 'id'
    },
    args: {
        type: Object,
        default: null
    }
});

const selected = ref(null);

const fetchTariffs = ({ pattern, page, limit }) => {
    const countryId =
        props.args?.address?.adrdefault?.city?.state?.country?.id ?? null;
    return backendApi()
        .shipmentRate()
        .index({
            agency: props.args?.agency?.id ?? null,
            shippingType:
                props.args?.shippingType === 'Ninguna'
                    ? null
                    : props.args?.shippingType,
            country: countryId || props.args?.country?.id,
            pattern: pattern ?? null,
            active: true,
            page,
            limit
        });
};

const {
    items: tariffs,
    loading: isLoading,
    showRetryButton,
    hasMore,
    searchQuery,
    getItems,
    loadMore,
    reset
} = useInfinite(fetchTariffs);

const handleSearch = () => {
    reset();
    getItems();
};

// Watch for agency ID changes
watch(
    () => props.args,
    (newArgs, oldArgs) => {
        if (JSON.stringify(newArgs) !== JSON.stringify(oldArgs)) {
            // Clear selection when agency changes
            selected.value = null;

            // Get fresh data
            handleSearch();
        }
    }
);

// Initial data fetch on mount
onMounted(() => {
    getItems();
});
</script>

<template>
    <AutocompleteFilter
        v-model="selected"
        v-model:search="searchQuery"
        :item-title="itemTitle"
        :item-value="itemValue"
        :items="tariffs"
        :label="hideLabel ? '' : $t('Tariff')"
        :loading="isLoading"
        :show-retry-button="showRetryButton"
        :infinite-scroll="hasMore"
        @infinite-scroll="loadMore"
        @update="emit('update', selected)"
        @preparing-search="handleSearch"
        @search="handleSearch"
        @retry="handleSearch"
    >
        <template v-slot:default="{ props, item }">
            <v-list-item
                v-bind="props"
                :title="`${item.raw.name}`"
                :subtitle="`${item.raw.shippingType}, ${item.raw.country?.name}`"
            >
            </v-list-item>
        </template>

        <template v-if="$slots.prepend" v-slot:prepend>
            <slot name="prepend"></slot>
        </template>
    </AutocompleteFilter>
</template>
